import React, { useState, Suspense } from "react";
import { preloadRoute } from "@/lib/lazyPages";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, MapPin, Cpu, ClipboardCheck, AlertTriangle,
  ListTodo, FileText, ShieldCheck, Users, Settings, LogOut, Menu, X, ChevronDown,
  Target, Handshake, GitBranch, DoorOpen, BookOpen, User, ScrollText, Loader2, HardHat,
} from "lucide-react";

const PageLoader = () => (
  <div data-testid="page-loader" className="flex min-h-[60vh] items-center justify-center">
    <Loader2 className="h-7 w-7 animate-spin text-primary/80" />
  </div>
);
import { Logo } from "@/components/Logo";
import { SessionManager } from "@/components/SessionManager";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL, isClientRole } from "@/components/ui-helpers";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Client-facing navigation (own establishment only)
const CLIENT_NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-tableau-de-bord", end: true },
  { to: "/etablissements", label: "Mon établissement", icon: Building2, testid: "nav-etablissements" },
  { to: "/equipements", label: "Mes équipements", icon: Cpu, testid: "nav-equipements" },
  { to: "/checkup", label: "Mon Check-up", icon: ClipboardCheck, testid: "nav-checkup" },
  { to: "/non-conformites", label: "Mes non-conformités", icon: AlertTriangle, testid: "nav-non-conformites" },
  { to: "/plan-action", label: "Mon plan d'action", icon: ListTodo, testid: "nav-plan-d-action" },
  { to: "/documents", label: "Mes documents", icon: FileText, testid: "nav-documents" },
  { to: "/services", label: "Services CEP", icon: ShieldCheck, testid: "nav-services-cep" },
];

// CEP navigation (Admin CEP + Expert CEP)
const CEP_NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, roles: ["admin_cep", "expert_cep"], testid: "nav-tableau-de-bord", end: true },
  { to: "/etablissements", label: "Établissements", icon: Building2, roles: ["admin_cep", "expert_cep"], testid: "nav-etablissements" },
  { to: "/prospects", label: "Prospects", icon: Target, roles: ["admin_cep", "expert_cep"], testid: "nav-prospects" },
  { to: "/opportunites", label: "Opportunités", icon: Handshake, roles: ["admin_cep", "expert_cep"], testid: "nav-opportunites" },
  { to: "/pipeline", label: "Pipeline", icon: GitBranch, roles: ["admin_cep", "expert_cep"], testid: "nav-pipeline" },
  { to: "/sites", label: "Sites", icon: MapPin, roles: ["admin_cep", "expert_cep"], testid: "nav-sites" },
  { to: "/locaux", label: "Locaux", icon: DoorOpen, roles: ["admin_cep", "expert_cep"], testid: "nav-locaux" },
  { to: "/equipements", label: "Équipements", icon: Cpu, roles: ["admin_cep", "expert_cep"], testid: "nav-equipements" },
  { to: "/autorisations", label: "Autorisations ARSN", icon: ShieldCheck, roles: ["admin_cep", "expert_cep"], testid: "nav-autorisations" },
  { to: "/travailleurs", label: "Travailleurs exposés", icon: HardHat, roles: ["admin_cep", "expert_cep"], testid: "nav-travailleurs" },
  { to: "/checkup", label: "Check-up", icon: ClipboardCheck, roles: ["admin_cep", "expert_cep"], testid: "nav-checkup" },
  { to: "/non-conformites", label: "Non-conformités", icon: AlertTriangle, roles: ["admin_cep", "expert_cep"], testid: "nav-non-conformites" },
  { to: "/plan-action", label: "Actions", icon: ListTodo, roles: ["admin_cep", "expert_cep"], testid: "nav-plan-d-action" },
  { to: "/documents", label: "Documents", icon: FileText, roles: ["admin_cep", "expert_cep"], testid: "nav-documents" },
  { to: "/services", label: "Services", icon: ShieldCheck, roles: ["admin_cep", "expert_cep"], testid: "nav-services-cep" },
  { to: "/utilisateurs", label: "Utilisateurs", icon: Users, roles: ["admin_cep", "expert_cep"], testid: "nav-utilisateurs" },
  { to: "/journal-audit", label: "Journal d'audit", icon: ScrollText, roles: ["admin_cep"], testid: "nav-journal-audit" },
  { to: "/referentiel", label: "Référentiel", icon: BookOpen, roles: ["admin_cep"], testid: "nav-referentiel" },
  { to: "/parametres", label: "Paramètres", icon: Settings, roles: ["admin_cep", "expert_cep"], testid: "nav-parametres" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const clientItems = user?.role === "client_admin"
    ? [
        ...CLIENT_NAV.slice(0, 3),
        { to: "/autorisations", label: "Autorisations ARSN", icon: ShieldCheck, testid: "nav-autorisations" },
        { to: "/travailleurs", label: "Travailleurs exposés", icon: HardHat, testid: "nav-travailleurs" },
        ...CLIENT_NAV.slice(3),
        { to: "/utilisateurs", label: "Utilisateurs", icon: Users, testid: "nav-utilisateurs" },
      ]
    : CLIENT_NAV;
  const items = isClientRole(user?.role) ? clientItems : CEP_NAV.filter((n) => n.roles.includes(user?.role));
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const SidebarContent = (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-5 py-5">
        <Logo size="sm" withSub={false} />
        <p className="mt-1 text-[9px] font-medium uppercase tracking-[0.22em] text-slate-500">Radioprotection Médicale</p>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            onMouseEnter={() => preloadRoute(n.to)}
            onFocus={() => preloadRoute(n.to)}
            onTouchStart={() => preloadRoute(n.to)}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary ring-1 ring-inset ring-primary/25"
                  : "text-slate-400 hover:bg-secondary hover:text-slate-100"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px]" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <div className="rounded-lg bg-secondary/60 px-3 py-2 text-[11px] leading-snug text-slate-500">
          <span className="font-semibold text-slate-400">De l'écart identifié</span> à la conformité démontrée.
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background grid-noise">
      <SessionManager />
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-border bg-card/50 lg:block">{SidebarContent}</aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64 border-r border-border bg-card">{SidebarContent}</div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-border glass px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="rounded-lg p-2 text-slate-400 hover:bg-secondary lg:hidden">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              {ROLE_LABEL[user?.role] || "Utilisateur"}
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-btn" className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-secondary">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-sm font-bold text-white">{initials}</div>
                <div className="hidden sm:block">
                  <p className="text-sm font-semibold leading-tight text-slate-100">{user?.name}</p>
                  <p className="text-xs leading-tight text-slate-500">{user?.job_title || user?.email}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-semibold text-slate-100">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profil")} onMouseEnter={() => preloadRoute("/profil")} data-testid="menu-profil">
                <User className="mr-2 h-4 w-4" /> Mon profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/parametres")} data-testid="menu-parametres">
                <Settings className="mr-2 h-4 w-4" /> Paramètres
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-400 focus:text-red-400">
                <LogOut className="mr-2 h-4 w-4" /> Se déconnecter
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="mx-auto max-w-7xl animate-fade-up">
            <Suspense key={pathname} fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
